const keyBy = require('lodash/keyBy')
const get = require('lodash/get')
const groupBy = require('lodash/groupBy')
const { MEMBERSHIPS } = require('../../location/tools/constants')
const { DIRECT_ORDER_TYPES } = require('../../allocation/config')
const { TOPUP, ROUTINE } = require('../../shipment/tools/planning-types')
const { getMarketVat } = require('../../product/tools')

const VALIDATION_REMARKS = {
  failed: 'exceeds-loan-balance',
  success: 'within-loan-balance',
  failedGetLoanBalance: 'failed-to-get-loan-balance'
}

const getRouteId = location => (
  get(location, 'joinedDocs.currentRoute._id') ||
  get(location, 'programs[0].services[0].routeId')
)

const calculateOrderTotalAmount = ({ order, productGroup, membershipType }) => {
  return Object.keys(order.products)
    .map(p => p.split(':')[1])
    .reduce((acc, sku) => {
      if (
        membershipType === MEMBERSHIPS.CLASSIC &&
        (order.products[`product:${sku}`].allocationType !== DIRECT_ORDER_TYPES.PAY_ON_DELIVERY)
      ) {
        return acc
      }
      acc += (productGroup[sku].skuPrice * order.products[`product:${sku}`].adjusted) + productGroup[sku].vat
      return acc
    }, 0)
}

async function validatePlanning (api, { orders, productGroup }) {
  const productsWithNoPrice = Object.keys(productGroup)
    .filter(sku => !productGroup[sku].skuPrice)
    .map(sku => `product:${sku}`)

  const validatedOrders = await Promise.all(
    orders.map(async (order) => {
      // Store inital products cause we filter out some subtypes during this check
      const initalProducts = order.products
      try {
        order.creator = await api.user.get(order.createdBy)
        order.destination = {}
        const destination = await api.location.restAdapter.getByFSID(order.destinationId)
        if (destination) {
          Object.assign(order.destination, {
            location: {
              ...destination,
              name: destination.name_of_pharmacy
            }
          })
          if (destination.supply_chain_level === 'sdp') {
            const membershipType = destination.membership_type
            // exclude orders that contain products with no sellprice
            order.productsWithNoPrice = Object.keys(order.products).filter(p => productsWithNoPrice.includes(p))
            if (order.productsWithNoPrice.length) {
              order.shouldExcluded = true
            }
            // for classic customers, if loan limit = 0 or is null then we should skip validation
            // if it's basic bypass check as well
            if (
              (membershipType === MEMBERSHIPS.CLASSIC && !destination.loan_limit) ||
              order.orderType === DIRECT_ORDER_TYPES.IMMEDIATE_PURCHASE
            ) {
              order.remark = VALIDATION_REMARKS.success

              return order
            }

            // all products for classic-payg-only customer has to be pay_on_delivery
            if (membershipType === MEMBERSHIPS.CLASSIC_PAYG_ONLY) {
              const productTypes = Object.values(order.products).map(p => p.allocationType)
              if (!productTypes.every(p => p === DIRECT_ORDER_TYPES.PAY_ON_DELIVERY)) {
                order.shouldExcluded = true
                return order
              }
            }

            // consider only POD products in order for classic
            if (membershipType === MEMBERSHIPS.CLASSIC) {
              const podProducts = Object.keys(order.products)
                .filter(p => order.products[p].allocationType === DIRECT_ORDER_TYPES.PAY_ON_DELIVERY)
                .map(product => ({ ...order.products[product], sku: product }))
              order.products = keyBy(podProducts, 'sku')
            }

            try {
              const walletOverview = await api.finances.getOverview(order.destinationId)
              const loanBalance = get(walletOverview, 'customer.creditBalance', 0)
              const loanLimit = get(walletOverview, 'customer.loanLimit', 0)
              order.totalAmount = calculateOrderTotalAmount({ order, membershipType, productGroup })
              if (order.orderType === ROUTINE) {
                order.shouldExcluded = order.totalAmount > loanBalance || Math.abs(order.totalAmount - loanBalance) > loanBalance
              } else if (order.orderType === TOPUP) {
                // the TOPUP order amount has already been subtracted from the loanBalance from api
                order.shouldExcluded = loanBalance < 0 || Math.abs(order.totalAmount + loanBalance) > loanLimit
              }
              order.remark = order.shouldExcluded ? VALIDATION_REMARKS.failed : VALIDATION_REMARKS.success
              order.shouldExcluded = !!order.productsWithNoPrice.length || order.shouldExcluded
            } catch (error) {
              console.warn(`cant get balance for ${order.destinationId}`, error)
            }
          }
        }
      } catch (error) {
        console.warn('Cant get creator', error, order.createdBy)
      }
      // Assign original products cause we want to display all sub types in planning module
      order.products = initalProducts
      return order
    })
  )
  const ordersValidated = validatedOrders.filter(order => order)
  return { orders: ordersValidated, productsWithNoPrice }
}

async function validateOrders (api, { orders, destinationIds, productMap, user }) {
  const uniqueDestinationIds = [...new Set(destinationIds)]
  const [{results: locations}, couchLocationDocs] = await Promise.all([
    api.location.pgAdapter.getList({ filter: { fsid: destinationIds }, skipCount: true }),
    api.location.getLocationsViaIds(uniqueDestinationIds, false, new Date())
  ])
  const locationsByFsid = groupBy(locations, 'fsid')
  const routeIdsByDestination = couchLocationDocs.reduce((routeIds, location) => {
    const routeId = getRouteId(location)
    if (routeId) routeIds[location._id] = routeId
    return routeIds
  }, {})
  const country = get(user, 'location.country')
  const currentVat = getMarketVat(country)
  const productsWithNoPrice = []
  const orderSellPrices = {}

  const validatedOrders = await Promise.all(
    orders.map(async (order) => {
      const uniqueProducts = Object.keys(order.products).map(p => `${p}`.replace('product:', ''))

      // We fetch the price of every sku for the given orders created at timestamps
      const pricePromises = order.ordersCreatedAt.map(timestamp => api.price.adapter.getPriceList({ skus: uniqueProducts, referenceDate: timestamp }))
      const prices = await Promise.all(pricePromises)
      const parsedPrices = []
      const transformedPrices = prices.map(priceGroup => {
        const { prices, referenceDate } = priceGroup
        const formattedPrices = prices.map(p => ({
          sellpriceId: p.sellpriceId,
          buypriceId: p.buypriceId,
          buyprice: p.buyprice,
          sellprice: p.sellprice,
          vat: p.vat,
          sku: `${p.sku}`.replace('product:', '')
        }))
        parsedPrices.push(...formattedPrices)
        return {
          referenceDate,
          prices: formattedPrices
        }
      })

      const pricesByReferenceDate = keyBy(transformedPrices, 'referenceDate')
      // store the sell price list linked to each order to pass on to shipments
      order.orderSnapshotIds.forEach(snapshotId => {
        orderSellPrices[snapshotId] = pricesByReferenceDate
      })
      const { productGroup } = api.order.tools.tallyOrderAmounts(productMap, parsedPrices, currentVat.vat)
      const skuNoSellPrices = Object.keys(productGroup)
        .filter(sku => !productGroup[sku].skuPrice)
        .map(sku => `product:${sku}`)
      productsWithNoPrice.push(...skuNoSellPrices)

      order.destination = {}
      const destination = get(locationsByFsid[order.destinationId], '0', null)
      if (destination) {
        Object.assign(order.destination, {
          location: {
            ...destination,
            name: destination.name_of_pharmacy
          }
        })
        if (destination.supply_chain_level === 'sdp') {
          const membershipType = destination.membership_type
          // exclude orders that contain products with no sellprice
          order.productsWithNoPrice = Object.keys(order.products).filter(p => skuNoSellPrices.includes(p))
          if (order.productsWithNoPrice.length) {
            order.shouldExcluded = true
          }
          // for classic customers, if loan limit = 0 or is null then we should skip validation
          // if it's basic bypass check as well
          if (
            (membershipType === MEMBERSHIPS.CLASSIC && !destination.loan_limit) ||
            order.orderType === DIRECT_ORDER_TYPES.IMMEDIATE_PURCHASE
          ) {
            order.remark = VALIDATION_REMARKS.success
            return order
          }
          // all products for classic-payg-only customer has to be pay_on_delivery
          if (membershipType === MEMBERSHIPS.CLASSIC_PAYG_ONLY) {
            const productTypes = Object.values(order.products).map(p => p.allocationType)
            if (!productTypes.every(p => p === DIRECT_ORDER_TYPES.PAY_ON_DELIVERY)) {
              order.shouldExcluded = true
              return order
            }
          }
          // consider only POD products in order for classic
          if (membershipType === MEMBERSHIPS.CLASSIC) {
            const podProducts = Object.keys(order.products)
              .filter(p => order.products[p].allocationType === DIRECT_ORDER_TYPES.PAY_ON_DELIVERY)
              .map(product => ({ ...order.products[product], sku: product }))
            order.products = keyBy(podProducts, 'sku')
          }
          try {
            const walletOverview = await api.finances.wallet.getOverview(destination.uuid, { excludeOrderId: order.orderId })
            const loanBalance = get(walletOverview, 'customer.creditBalance', 0)
            const loanLimit = get(walletOverview, 'customer.loanLimit', 0)
            order.totalAmount = calculateOrderTotalAmount({ order, membershipType, productGroup })
            if (order.orderType === ROUTINE) {
              order.shouldExcluded = order.totalAmount > loanBalance || Math.abs(order.totalAmount - loanBalance) > loanBalance
            } else if (order.orderType === TOPUP) {
              // the TOPUP order amount has already been subtracted from the loanBalance from api
              order.shouldExcluded = loanBalance < 0 || Math.abs(order.totalAmount + loanBalance) > loanLimit
            }
            order.remark = order.shouldExcluded ? VALIDATION_REMARKS.failed : VALIDATION_REMARKS.success
            order.shouldExcluded = !!order.productsWithNoPrice.length || order.shouldExcluded
          } catch (error) {
            order.shouldExcluded = true
            order.remark = VALIDATION_REMARKS.failedGetLoanBalance
          }
        }
      }
      return order
    })
  )
  const ordersValidated = validatedOrders.filter(order => order)
  return { orders: ordersValidated, productsWithNoPrice, orderSellPrices, routeIdsByDestination }
}

exports.validatePlanning = validatePlanning
exports.validateOrders = validateOrders
exports.VALIDATION_REMARKS = VALIDATION_REMARKS
